/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as Ui from './styles';
import headerStyleHander from './headerStyleHandler';
import { filterData } from './tableFilterHandler';
import { checkResultadoCruzamento } from '../../stores/actions/simpleCross';
import DefaultTable from './components/DefaultTable';
import MainQuestionIsNotGradeTable from './components/MainQuestionIsNotGradeTable';
import MainQuestionIsGrade from './components/MainQuestionIsGrade';
import AllQuestionGradeTable from './components/AllQuestionGradeTable';
import { Loading } from '../Loading';
import { getMainColumnGroups } from './utils/getMainColumnGroups';
import { isGradeQuestion } from './utils/isGradeQuestion';

const TABLE_COMPONENTS = {
  'false-true': MainQuestionIsNotGradeTable,
  'true-false': MainQuestionIsGrade,
  'true-true': AllQuestionGradeTable,
  'false-false': DefaultTable,
};

export default function Table({ customerId, surveyId, data, filtering, isFullScreen }) {
  const [dataToShow, setDataToShow] = useState({});
  // headerStyle data
  const [headerHeight, setHeaderHeight] = useState('0px');
  const [headerWidthData, setHeaderWidthData] = useState({ perguntaWidth: '0px', alternativasWidth: ['0px'] });
  const dispatch = useDispatch();

  const perguntaPrincipal = dataToShow?.perguntas_principais?.[0];
  const mainQuestionIsGrade = isGradeQuestion(perguntaPrincipal?.alternativas);

  // Ordem canônica das colunas, decidida uma vez para a tabela inteira: header,
  // todos os tbodies e o footer consomem daqui.
  const { groups: columnGroups, labelKey: columnLabelKey } = getMainColumnGroups(
    perguntaPrincipal?.alternativas,
    perguntaPrincipal?.perguntas_cruzadas,
  );
  const orderedMainAlternatives = columnGroups.flatMap((grupo) => grupo.alternativas);

  const updateDataToShow = useCallback(() => {
    if (!data || !data.perguntas_principais || !data.perguntas_principais[0]) {
      setDataToShow(null);
      return;
    }
    const perguntasFiltradas = filterData(data.perguntas_principais[0].perguntas_cruzadas, filtering);
    // resetando referencias, ja que nao eh uma deep copy
    const newData = data;
    newData.perguntas_principais = [...data.perguntas_principais];
    newData.perguntas_principais[0] = { ...data.perguntas_principais[0] };
    newData.perguntas_principais[0].perguntas_cruzadas = perguntasFiltradas;
    setDataToShow(newData);
  }, [data, filtering]);

  /**
   * TODO:
   * - Trazer header e footer da tabela de volta para o componente atual
   * - Garantir que em multiplas seleções as tabelas sejam encadeadas de 
   * forma corretaJa com
   */

  const renderTBody = () => {
    if (!perguntaPrincipal) return null;

    return perguntaPrincipal.perguntas_cruzadas.map((pergunta, index) => {
      const key = `${mainQuestionIsGrade}-${isGradeQuestion(pergunta.alternativas)}`;
      const TableComponent = TABLE_COMPONENTS[key] || DefaultTable;

      const dynamicDataToShow = {
        ...dataToShow,
        perguntas_principais: [
          {
            ...perguntaPrincipal,
            perguntas_cruzadas: [pergunta],
          },
        ],
      };

      return (
        <TableComponent
          key={index}
          dataToShow={dynamicDataToShow}
          headerHeight={headerHeight}
          mainAlternatives={orderedMainAlternatives}
          columnLabelKey={columnLabelKey}
          {...(TableComponent === DefaultTable ? { onSelect: handleSelect } : {})}
        />
      );
    });
  };

  // As larguras são calculadas na ordem crua do array, mas as colunas são
  // renderizadas na ordem canônica — o vínculo é pela alternativa, não pela posição.
  const widthByAlternativaId = new Map(
    (perguntaPrincipal?.alternativas ?? []).map((alternativa, index) => [
      alternativa.id, headerWidthData.alternativasWidth[index],
    ])
  );

  /**
   * Colunas do header: sem agrupamento (principal não é grade) cada alternativa é
   * uma coluna; na grade, um th por grupo cobre as colunas que ele reúne.
   */
  const renderColumnHeaders = () => {
    const [primeiroGrupo] = columnGroups;

    if (primeiroGrupo?.label === null) {
      return primeiroGrupo.alternativas.map((alternativa) => (
        <th key={alternativa.id} style={{ minWidth: widthByAlternativaId.get(alternativa.id) }}>
          {alternativa.enunciado}
        </th>
      ));
    }

    return columnGroups.map((grupo) => (
      <th key={grupo.label} colSpan={grupo.alternativas.length}>{grupo.label}</th>
    ));
  };

  // Renderiza o header da tabela
  const renderTHeader = () => {
    if (!perguntaPrincipal || !perguntaPrincipal.perguntas_cruzadas?.[0]) return null;

    return (
      <thead ref={computHeaderSize}>
        <tr>
          <th>{perguntaPrincipal.enunciado}</th>
          <th className="respondentes" colSpan="2">Respondentes</th>
          {renderColumnHeaders()}
        </tr>
      </thead>
    );
  };

  // Renderiza o footer da tabela
  const renderTFooter = () => {
    if (!perguntaPrincipal || !perguntaPrincipal.perguntas_cruzadas?.[0]) return null;

    return (
      <tfoot>
        <tr>
          <td style={{ minWidth: headerWidthData.perguntaWidth }}>Totais:</td>
          <td colSpan="2">---</td>
          {orderedMainAlternatives.map((alternativa) => (
            <td key={alternativa.id} style={{ minWidth: widthByAlternativaId.get(alternativa.id) }}>
              <span>{`${alternativa.resultado_percentual}%`}</span>
            </td>
          ))}
        </tr>
      </tfoot>
    );
  }

  useEffect(() => {
    updateDataToShow();
  }, [updateDataToShow]);

  const computHeaderSize = (node) => {
    const computedHeight = headerStyleHander.computHeaderSize(node);
    if (computedHeight && computedHeight !== headerHeight) {
      setHeaderHeight(computedHeight);
    }
  };

  const computTableSize = (node) => {
    const computedHeaderWidthData = headerStyleHander.computTableSize(node, dataToShow, 0.25, 1, 1, 136);

    const isAlternativasChanged = () => headerWidthData.alternativasWidth.length !== computedHeaderWidthData.alternativasWidth.length
      && headerWidthData.alternativasWidth.some((value, index) => value !== computedHeaderWidthData.alternativasWidth[index]);

    if (computedHeaderWidthData && isAlternativasChanged()) {
      setHeaderWidthData({
        perguntaWidth: computedHeaderWidthData.perguntaWidth,
        alternativasWidth: computedHeaderWidthData.alternativasWidth,
      });
    }
  };

  const handleSelect = (resultadoCruzamento) => {
    if (!resultadoCruzamento.marcacao) {
      resultadoCruzamento.marcacao = { is_checked: true };
    } else {
      resultadoCruzamento.marcacao.is_checked = !resultadoCruzamento.marcacao.is_checked;
    }
    // Fora do if/else: a primeira marcacao de uma celula que nunca teve
    // `marcacao` no payload tambem precisa repintar.
    setDataToShow({ ...dataToShow });
    const newCheckStatus = resultadoCruzamento.marcacao.is_checked;
    checkResultadoCruzamento(customerId, surveyId, resultadoCruzamento.id_cruzamento, newCheckStatus, dispatch);
  };

  return (
    <Ui.Container ref={computTableSize} isFullScreen={isFullScreen}>
      {dataToShow?.perguntas_principais ? (
        <Ui.Table>
          {renderTHeader()}
          {renderTBody()}
          {renderTFooter()}
        </Ui.Table>
      ) : (
        <Loading size={30} running />
      )}
    </Ui.Container>
  );
}
